import React from "react";
import { Heart, Trash2, CornerDownRight, Shield, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

/**
 * Generic comment renderer supporting one level of replies + admin badge.
 *
 * Props:
 *  - comment: { id, user_name, user_picture, text, created_at, is_admin, liked, likes, user_id, parent_id }
 *  - replies: list of reply comments (only for top-level)
 *  - isReply: boolean — controls indentation/avatar size
 *  - isRTL, lang
 *  - user (auth user or null), isAdmin (admin role)
 *  - testidPrefix: string base for data-testid (e.g. "recipe-comment", "academy-comment", "enc-comment")
 *  - replyState: { replyTo, replyText, setReplyText, replySubmitting, openReply, cancelReply, submitReply }
 *  - onLike(id), onDelete(id), onReplyToggle(id)
 *  - renderComment: function reference for recursive rendering of replies
 */
export default function CommentItem({
  comment: c,
  replies = [],
  isReply = false,
  isRTL,
  user,
  isAdmin,
  testidPrefix,
  replyState,
  onLike,
  onDelete,
  renderChild,
}) {
  const { replyTo, replyText, setReplyText, replySubmitting, openReply, cancelReply, submitReply } = replyState;
  return (
    <div
      id={`comment-${c.id}`}
      className={`scroll-mt-24 ${isReply ? "" : "space-y-2"}`}
      data-testid={`${testidPrefix}-${c.id}`}
    >
      <div
        className={`flex gap-3 group p-2 -mx-2 rounded-lg transition-shadow ${
          c.is_admin ? "bg-[#FFF4EE] border border-[#C86240]/30 ring-1 ring-[#C86240]/10" : ""
        }`}
      >
        {c.user_picture ? (
          <img
            src={c.user_picture}
            alt=""
            className={`${isReply ? "w-7 h-7" : "w-9 h-9"} rounded-full border border-[#2A2421]/10 shrink-0`}
          />
        ) : (
          <div
            className={`${
              isReply ? "w-7 h-7 text-xs" : "w-9 h-9 text-sm"
            } rounded-full bg-[#C86240]/15 flex items-center justify-center text-[#C86240] font-semibold shrink-0`}
          >
            {(c.user_name || "?").charAt(0)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <span className="font-semibold text-[#2A2421]">{c.user_name}</span>
            {c.is_admin && (
              <span
                className="inline-flex items-center gap-1 bg-[#C86240] text-white px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide"
                data-testid={`${testidPrefix}-admin-badge-${c.id}`}
              >
                <Shield className="w-2.5 h-2.5" />
                {isRTL ? "إدارة" : "Admin"}
              </span>
            )}
            <span className="text-[#5C544F]">·</span>
            <span className="text-[#5C544F]">{new Date(c.created_at).toLocaleDateString()}</span>
          </div>
          <div className="mt-1 text-sm text-[#2A2421] whitespace-pre-wrap leading-relaxed">{c.text}</div>
          <div className="mt-1.5 flex items-center gap-3">
            <button
              onClick={() => onLike(c.id)}
              className={`inline-flex items-center gap-1 text-xs transition-colors ${
                c.liked ? "text-[#C86240]" : "text-[#5C544F] hover:text-[#C86240]"
              }`}
              data-testid={`${testidPrefix}-like-${c.id}`}
              title={isRTL ? "إعجاب" : "Like"}
            >
              <Heart className={`w-3.5 h-3.5 ${c.liked ? "fill-[#C86240]" : ""}`} />
              <span>{c.likes || 0}</span>
            </button>
            {!isReply && user && (
              <button
                onClick={() => openReply(c.id)}
                className="inline-flex items-center gap-1 text-xs text-[#5C544F] hover:text-[#C86240] transition-colors"
                data-testid={`${testidPrefix}-reply-${c.id}`}
              >
                <CornerDownRight className="w-3.5 h-3.5" />
                {isRTL ? "رد" : "Reply"}
              </button>
            )}
          </div>
        </div>
        {(isAdmin || (user && c.user_id === user.user_id)) && (
          <button
            onClick={() => onDelete(c.id)}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 p-1 self-start"
            data-testid={`${testidPrefix}-delete-${c.id}`}
            title={isRTL ? "حذف" : "Delete"}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {!isReply && (
        <div className={`${isRTL ? "pr-10 border-r-2" : "pl-10 border-l-2"} border-[#C86240]/20 space-y-2`}>
          {replies.map((r) => renderChild(r))}
          {replyTo === c.id && (
            <div className="bg-white rounded-xl p-2.5 border border-[#C86240]/30">
              <Textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={isRTL ? `الرد على ${c.user_name}...` : `Reply to ${c.user_name}...`}
                rows={2}
                maxLength={2000}
                dir={isRTL ? "rtl" : "ltr"}
                className="bg-[#F9F6F0] border-[#2A2421]/15 text-sm"
                data-testid={`${testidPrefix}-reply-input-${c.id}`}
              />
              <div className="flex items-center justify-end gap-2 mt-1.5">
                <Button
                  onClick={cancelReply}
                  size="sm"
                  variant="ghost"
                  className="rounded-full text-xs"
                  data-testid={`${testidPrefix}-reply-cancel-${c.id}`}
                >
                  {isRTL ? "إلغاء" : "Cancel"}
                </Button>
                <Button
                  onClick={() => submitReply(c.id)}
                  disabled={replySubmitting || replyText.trim().length < 3}
                  size="sm"
                  className="bg-[#C86240] hover:bg-[#b15637] text-white rounded-full text-xs"
                  data-testid={`${testidPrefix}-reply-submit-${c.id}`}
                >
                  <Send className="w-3 h-3 me-1" /> {isRTL ? "نشر الرد" : "Post reply"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
